import { useState } from 'react';
import { useGetUserProfile } from '@leagueplatform/web-common';
import { setUserProfile } from '@leagueplatform/user-profile-api';

/**
 * Reads and persists the member's "Allow email notifications" preference for the
 * Notifications screen (GEIS-446 / PRD S-4).
 *
 * The toggle maps to the League-native `opted_into_marketing_communications`
 * flag on the user profile — the same field the mobile apps drive (iOS
 * GEIS-341, Android GEIS-349). Web is email-only, so this is the only toggle.
 *
 * Saving is optimistic: the toggle flips immediately, the change is persisted
 * via the League user-profile API, and on failure the toggle reverts and an
 * inline error is surfaced. State is derived from the `useGetUserProfile` query
 * (no mirrored copy), so a plan switch — which refetches the profile — is
 * reflected automatically.
 */
export const useEmailNotificationPreference = () => {
  const { data, isLoading, refetch } = useGetUserProfile();

  // The optimistic value held while a save is in flight; `null` means "show
  // whatever the server reports".
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const serverEnabled =
    data?.userProfile?.optedIntoMarketingCommunications ?? false;
  const isEnabled = pendingEnabled ?? serverEnabled;

  const toggle = async () => {
    if (isSaving) return;

    const nextEnabled = !isEnabled;
    setPendingEnabled(nextEnabled);
    setHasSaveError(false);
    setIsSaving(true);

    try {
      await setUserProfile({
        opted_into_marketing_communications: nextEnabled,
      });
    } catch {
      // The write itself failed — revert the optimistic toggle and surface the
      // error.
      setPendingEnabled(null);
      setHasSaveError(true);
      setIsSaving(false);
      return;
    }

    // The write succeeded. Re-read so the screen reflects the source of truth
    // before dropping the optimistic value (avoids a flicker back to the stale
    // cached value). In react-query v4 refetch() RESOLVES with an error result
    // rather than rejecting, so inspect the result: drop the optimistic value
    // only once the refreshed server value is known to reflect the save. If the
    // refresh failed, keep the optimistic value — the write already succeeded.
    let refreshed = false;
    try {
      const result = await refetch();
      refreshed = !result?.isError;
    } catch {
      // Only reachable if refetch is configured to throw; treat as not refreshed.
    }
    if (refreshed) {
      setPendingEnabled(null);
    }
    setIsSaving(false);
  };

  return {
    isEnabled,
    isLoading,
    // Error state = "no profile to show": covers an outright load failure and a
    // resolved-but-empty profile (`data: undefined`), so we never render the
    // toggle in a default-off state without a source of truth. We intentionally
    // do NOT fold in react-query's `isError` flag directly: a *refetch* failure
    // after the initial load leaves `isError` true while retaining the last good
    // `data`, and that transient failure must not blow the whole screen away
    // (the save may well have succeeded). Differs from `usePersonalInfo`, which
    // never refetches after a mutation.
    isError: !data,
    isSaving,
    hasSaveError,
    toggle,
  };
};
